import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faChevronLeft,
  faChevronRight,
  faSave,
  faCheckCircle,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { buildPreviewChallenge } from "utils/challenge";
import { colors } from "utils";
import { ConsensusActivityRender } from "../../Session/Activity/activities/ConsensusActivity/ConsensusActivityRender";
import {
  SectionCard,
  HeaderRow,
  CopyIconButton,
  monoValueStyle,
  fmtDate,
} from "./displayPrimitives";
import {
  fetchLcReviewBatches,
  fetchLcReviewQueue,
  fetchLcReviewItem,
  saveLcReview,
  syncLcReviewToSheet,
  isEndpointMissing,
  QUALITY_GATE_OPTIONS,
  REJECTION_TAG_OPTIONS,
  LC_ALIGNMENT_OPTIONS,
  CONFIDENCE_OPTIONS,
} from "./evaluatorApi";

// Sentinel for the "All batches" option in the batch selector. Distinct from any
// real batch name and from "" (the pre-load-not-yet-selected sentinel).
const ALL_BATCHES_VALUE = "__all_batches__";

// ─── Local sub-components ──────────────────────────────────────────────────────

const EndpointPending = ({ label }) => (
  <div style={{ fontSize: "0.85em", color: "#b06a00", fontStyle: "italic" }}>
    {label} endpoint not available yet — pending backend.
  </div>
);

const FieldLabel = ({ children }) => (
  <div
    style={{
      textTransform: "uppercase",
      fontSize: "0.68em",
      fontWeight: 600,
      letterSpacing: "0.05em",
      color: "#8a8a8a",
      marginBottom: "4px",
    }}
  >
    {children}
  </div>
);

const FormField = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
    <FieldLabel>{label}</FieldLabel>
    {children}
  </div>
);

const radioGroupStyle = { display: "flex", gap: "16px", flexWrap: "wrap" };
const radioLabelStyle = { display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "0.9em" };
const textareaStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  resize: "vertical",
  fontFamily: "inherit",
  fontSize: "0.9em",
};
const selectStyle = {
  padding: "6px 10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "0.9em",
  width: "100%",
};

// ─── Queue sidebar (compact list of items with reviewed indicator) ─────────────

const QueueSidebar = ({ items, currentPairId, onSelectPairId }) => (
  <div
    style={{
      flex: "0 0 180px",
      maxHeight: "70vh",
      overflowY: "auto",
      borderRight: "1px solid #eee",
      paddingRight: "8px",
    }}
  >
    {items.map((item, idx) => (
      <div
        key={item.pair_id || idx}
        onClick={() => onSelectPairId(item.pair_id)}
        title={`${item.question_id} / ${item.lc_id}`}
        style={{
          padding: "7px 10px",
          borderRadius: "6px",
          cursor: "pointer",
          background: item.pair_id === currentPairId ? colors.blue50 : "transparent",
          border:
            item.pair_id === currentPairId
              ? `1px solid ${colors.blue200}`
              : "1px solid transparent",
          marginBottom: "4px",
          fontSize: "0.78em",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <FontAwesomeIcon
          icon={faCheckCircle}
          style={{ color: item.reviewed ? colors.green600 || "#2a9d5c" : "#ddd", flexShrink: 0 }}
        />
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            wordBreak: "break-all",
          }}
        >
          {idx + 1}. {(item.standard_code || "").slice(0, 12)}
          {item.lc_id ? ` / ${String(item.lc_id).slice(0, 8)}…` : ""}
        </span>
      </div>
    ))}
  </div>
);

// ─── Right pane: LC/CCSS context + 8-field annotation form ────────────────────

const LcReviewForm = ({ queueItem, itemData, saving, onSave }) => {
  const review = itemData?.existing_review || null;
  const lc = itemData?.lc || null;
  const ccss = itemData?.ccss || null;

  const [qualityGate, setQualityGate] = useState(review?.quality_gate || "");
  const [rejectionTag, setRejectionTag] = useState(review?.rejection_tag || "");
  const [workedSolution, setWorkedSolution] = useState(review?.worked_solution || "");
  const [softFlag, setSoftFlag] = useState(review?.soft_flag || "");
  const [itemSolve, setItemSolve] = useState(review?.item_solve || "");
  const [lcAlignment, setLcAlignment] = useState(review?.lc_alignment || "");
  const [confidence, setConfidence] = useState(review?.annotator_confidence || "");
  const [reasoning, setReasoning] = useState(review?.annotator_reasoning || "");

  // Reset form fields when the queue item changes (new pair loaded).
  useEffect(() => {
    const r = itemData?.existing_review || null;
    setQualityGate(r?.quality_gate || "");
    setRejectionTag(r?.rejection_tag || "");
    setWorkedSolution(r?.worked_solution || "");
    setSoftFlag(r?.soft_flag || "");
    setItemSolve(r?.item_solve || "");
    setLcAlignment(r?.lc_alignment || "");
    setConfidence(r?.annotator_confidence || "");
    setReasoning(r?.annotator_reasoning || "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueItem?.pair_id]);

  const handleSave = () => {
    onSave({
      question_id: queueItem.question_id,
      question_type: queueItem.question_type,
      lc_id: queueItem.lc_id,
      quality_gate: qualityGate || null,
      rejection_tag: rejectionTag || null,
      worked_solution: workedSolution || null,
      soft_flag: softFlag || null,
      item_solve: itemSolve || null,
      lc_alignment: lcAlignment || null,
      annotator_confidence: confidence || null,
      annotator_reasoning: reasoning || null,
    });
  };


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {/* LC context */}
      <SectionCard title="Learning Component">
        <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <HeaderRow label="LC ID">
            {lc?.lc_id ? (
              <>
                <span style={monoValueStyle}>{lc.lc_id}</span>
                <CopyIconButton value={lc.lc_id} title="Copy LC ID" />
              </>
            ) : (
              <span style={{ color: "#aaa" }}>—</span>
            )}
          </HeaderRow>
          {lc?.lc_description && (
            <div style={{ fontSize: "0.88em", color: "#333", marginTop: "4px" }}>
              {lc.lc_description}
            </div>
          )}
        </div>
      </SectionCard>

      {/* CCSS context */}
      <SectionCard title="CCSS Standard">
        {ccss ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <HeaderRow label="Code">
              <span style={{ fontWeight: 600 }}>{ccss.code}</span>
              <CopyIconButton value={ccss.code} title="Copy CCSS code" />
            </HeaderRow>
            {ccss.short_name && (
              <HeaderRow label="Short Name">
                <span style={{ fontSize: "0.88em" }}>{ccss.short_name}</span>
              </HeaderRow>
            )}
            {ccss.full_description && (
              <div style={{ fontSize: "0.85em", color: "#555", marginTop: "4px" }}>
                {ccss.full_description}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#aaa", fontSize: "0.85em" }}>No CCSS data.</span>
        )}
      </SectionCard>

      {/* 8-field annotation form */}
      <SectionCard title="Annotation" style={{ background: "#fbfcff" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

          {/* 1. Quality Gate */}
          <FormField label="1. Quality Gate">
            <div style={radioGroupStyle}>
              {QUALITY_GATE_OPTIONS.map((o) => (
                <label key={o.value} style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="quality_gate"
                    value={o.value}
                    checked={qualityGate === o.value}
                    onChange={() => setQualityGate(o.value)}
                    disabled={saving}
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </FormField>

          {/* 2. Rejection tag (always visible, mirrors the sheet column) */}
          <FormField label="2. Rejection Tag">
            <select
              value={rejectionTag}
              onChange={(e) => setRejectionTag(e.target.value)}
              disabled={saving}
              style={selectStyle}
            >
              <option value="">— select —</option>
              {REJECTION_TAG_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>

          {/* 3. Worked solution (always visible, mirrors the sheet column) */}
          <FormField label="3. Worked Solution">
            <textarea
              value={workedSolution}
              onChange={(e) => setWorkedSolution(e.target.value)}
              disabled={saving}
              rows={3}
              placeholder="Provide the correct worked solution (if the answer key is wrong)…"
              style={textareaStyle}
            />
          </FormField>

          {/* 4. Soft flag (always) */}
          <FormField label="4. Soft Flag">
            <textarea
              value={softFlag}
              onChange={(e) => setSoftFlag(e.target.value)}
              disabled={saving}
              rows={2}
              placeholder="Optional note or flag…"
              style={textareaStyle}
            />
          </FormField>

          {/* 5. Item solve (always) */}
          <FormField label="5. Item Solve">
            <textarea
              value={itemSolve}
              onChange={(e) => setItemSolve(e.target.value)}
              disabled={saving}
              rows={3}
              placeholder="How you would solve this item…"
              style={textareaStyle}
            />
          </FormField>

          {/* 6. LC Alignment (always visible dropdown, mirrors the sheet column) */}
          <FormField label="6. LC Alignment">
            <select
              value={lcAlignment}
              onChange={(e) => setLcAlignment(e.target.value)}
              disabled={saving}
              style={selectStyle}
            >
              <option value="">— select —</option>
              {LC_ALIGNMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>

          {/* 7. Confidence (always) */}
          <FormField label="7. Confidence">
            <select
              value={confidence}
              onChange={(e) => setConfidence(e.target.value)}
              disabled={saving}
              style={selectStyle}
            >
              <option value="">— select —</option>
              {CONFIDENCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FormField>

          {/* 8. Reasoning (always) */}
          <FormField label="8. Reasoning">
            <textarea
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              disabled={saving}
              rows={4}
              placeholder="Explain your judgment…"
              style={textareaStyle}
            />
          </FormField>

          {/* TODO(OKO2-3166): AI rating panel + blind toggle go here once the
              AI evaluation batch (create_evaluation_batch) is wired up. */}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              alignSelf: "flex-start",
              background: colors.blue500,
              color: "white",
              border: "none",
              borderRadius: "20px",
              padding: "8px 18px",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
              fontSize: "14px",
            }}
          >
            {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
            &nbsp;Save
          </button>
        </div>
      </SectionCard>

      {review?.updated_at && (
        <div style={{ fontSize: "0.8em", color: "#888" }}>
          Last saved: {fmtDate(review.updated_at)}
          {review.sme_email ? ` by ${review.sme_email}` : ""}
        </div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

export const LcAlignmentReview = () => {
  const location = useLocation();

  // Deep-link params from the URL (?batch=&question=&lc=), read once on load.
  // The Sheet export (OKO2-3395) links each row here with this exact query
  // contract: {app_base}/admin/evaluator-review/lc-alignment?batch=<url-encoded
  // batch_name>&question=<question_id>&lc=<lc_id>. Empty on a normal visit.
  const [deepLinkParams] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      batch: params.get("batch"),
      question: params.get("question"),
      lc: params.get("lc"),
    };
  });
  // Pending pair to select once the deep-linked batch's queue has loaded.
  // Cleared after one attempt (match or no match) or if the batch is changed
  // away from the deep-linked one, so it never re-fires on later navigation.
  const [pendingPairParams, setPendingPairParams] = useState(() =>
    deepLinkParams.question && deepLinkParams.lc
      ? { question: deepLinkParams.question, lc: deepLinkParams.lc }
      : null
  );

  // Batch state
  const [batches, setBatches] = useState([]);
  const [batchesPending, setBatchesPending] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("");

  // Queue state
  const [queueItems, setQueueItems] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queuePending, setQueuePending] = useState(false);
  const [queueError, setQueueError] = useState("");

  // Current position in the queue, tracked by pair_id (not index) so filtering
  // and save-triggered reviewed-flips never yank the open item out from under
  // the user mid-interaction — only explicit navigation moves it.
  const [currentPairId, setCurrentPairId] = useState(null);

  // Filter state (all client-side, derived from the currently-loaded queueItems)
  const [reviewStatusFilter, setReviewStatusFilter] = useState("all"); // all | open | reviewed
  const [ccssFilter, setCcssFilter] = useState("all");
  const [lcFilter, setLcFilter] = useState("all");
  const [questionIdSearch, setQuestionIdSearch] = useState("");

  // Item detail state (loaded per pair)
  const [itemData, setItemData] = useState(null);
  const [itemLoading, setItemLoading] = useState(false);
  const [itemPending, setItemPending] = useState(false);
  const [itemError, setItemError] = useState("");

  // Save state
  const [saving, setSaving] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  // "Refresh to Sheet" state (OKO2-3395 manual fallback for the 30-min cron)
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [sheetSyncPending, setSheetSyncPending] = useState(false);
  const [sheetSyncError, setSheetSyncError] = useState("");
  const [sheetSyncResult, setSheetSyncResult] = useState(null);

  const previewRef = useRef(null);

  // Load batches on mount
  useEffect(() => {
    fetchLcReviewBatches()
      .then((res) => {
        const list = res?.batches || [];
        setBatches(list);
        if (deepLinkParams.batch) {
          setSelectedBatch(deepLinkParams.batch);
        } else if (list.length > 0) {
          setSelectedBatch(list[0].name);
        }
      })
      .catch((e) => {
        if (isEndpointMissing(e)) setBatchesPending(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load queue whenever selected batch changes
  useEffect(() => {
    if (!selectedBatch) return;
    // If the user (or a stale deep link with no matching queue) moves off the
    // deep-linked batch, drop the pending pair so it never applies to an
    // unrelated batch's queue.
    if (selectedBatch !== deepLinkParams.batch) setPendingPairParams(null);
    let cancelled = false;
    setQueueLoading(true);
    setQueuePending(false);
    setQueueError("");
    setQueueItems([]);
    setCurrentPairId(null);
    setItemData(null);
    fetchLcReviewQueue(selectedBatch === ALL_BATCHES_VALUE ? null : selectedBatch)
      .then((res) => {
        if (cancelled) return;
        setQueueItems(res?.items || []);
        setQueueTotal(res?.total ?? 0);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isEndpointMissing(e)) setQueuePending(true);
        else setQueueError("Failed to load queue.");
      })
      .finally(() => {
        if (!cancelled) setQueueLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedBatch, deepLinkParams.batch]);

  // Derived CCSS/LC dropdown options from the full loaded queue (not narrowed by
  // the other filters — they always list every code/LC present in the batch).
  const ccssOptions = useMemo(() => {
    const codes = new Set();
    queueItems.forEach((it) => {
      if (it.standard_code) codes.add(it.standard_code);
    });
    return Array.from(codes).sort();
  }, [queueItems]);

  const lcOptions = useMemo(() => {
    const ids = new Set();
    queueItems.forEach((it) => {
      if (it.lc_id) ids.add(it.lc_id);
    });
    return Array.from(ids).sort();
  }, [queueItems]);

  // Client-side filtered queue (Review Status / CCSS / LC / Question ID search)
  const filteredItems = useMemo(() => {
    const q = questionIdSearch.trim().toLowerCase();
    return queueItems.filter((it) => {
      if (reviewStatusFilter === "open" && it.reviewed) return false;
      if (reviewStatusFilter === "reviewed" && !it.reviewed) return false;
      if (ccssFilter !== "all" && it.standard_code !== ccssFilter) return false;
      if (lcFilter !== "all" && it.lc_id !== lcFilter) return false;
      if (q && !String(it.question_id).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [queueItems, reviewStatusFilter, ccssFilter, lcFilter, questionIdSearch]);

  // The currently-open item, looked up in the FULL (unfiltered) queue so a
  // save-triggered filter exclusion never yanks it out from under the user —
  // only explicit navigation (Prev/Next/sidebar click) changes currentPairId.
  const currentQueueItem = queueItems.find((it) => it.pair_id === currentPairId) || null;

  // Keep currentPairId pointing at a valid, currently-visible (filtered) item:
  // resets to the first filtered item whenever the filters change, the queue
  // reloads, or the current item drops out of the filtered view.
  useEffect(() => {
    if (filteredItems.length === 0) {
      setCurrentPairId(null);
      return;
    }
    if (!filteredItems.some((it) => it.pair_id === currentPairId)) {
      setCurrentPairId(filteredItems[0].pair_id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems]);

  // Resolve the deep-linked question+lc pair once the queue for the
  // deep-linked batch has loaded. Runs only while pendingPairParams is set
  // (the URL had both `question` and `lc`); clears itself after one attempt
  // so it never overrides later, explicit navigation. No match (stale link,
  // wrong batch) falls back silently to the default first-item selection the
  // effect above already applied — no crash, no new UI.
  useEffect(() => {
    if (!pendingPairParams) return;
    if (filteredItems.length === 0) return;
    const match = filteredItems.find(
      (it) =>
        String(it.question_id) === String(pendingPairParams.question) &&
        String(it.lc_id) === String(pendingPairParams.lc)
    );
    if (match) setCurrentPairId(match.pair_id);
    setPendingPairParams(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems, pendingPairParams]);

  // Load item detail whenever queue + currentPairId change
  useEffect(() => {
    if (!currentQueueItem) return undefined;
    let cancelled = false;
    setItemLoading(true);
    setItemPending(false);
    setItemError("");
    setItemData(null);
    fetchLcReviewItem({
      questionId: currentQueueItem.question_id,
      questionType: currentQueueItem.question_type,
      lcId: currentQueueItem.lc_id,
      standardCode: currentQueueItem.standard_code,
    })
      .then((res) => {
        if (!cancelled) setItemData(res);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isEndpointMissing(e)) setItemPending(true);
        else setItemError("Failed to load item.");
      })
      .finally(() => {
        if (!cancelled) setItemLoading(false);
      });
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQueueItem?.pair_id]);

  // Trigger MathJax typeset when item changes (same pattern as EvaluatorReview)
  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      const mj = window.MathJax;
      try {
        if (mj.typesetPromise) mj.typesetPromise();
        else mj.typeset();
      } catch (e) {
        /* ignore */
      }
    };
    const ready = () => {
      const mj = typeof window !== "undefined" ? window.MathJax : null;
      if (!mj || !(mj.typesetPromise || mj.typeset)) return false;
      if (mj.startup && mj.startup.promise && mj.startup.promise.then) {
        mj.startup.promise.then(run);
      } else {
        run();
      }
      return true;
    };
    if (ready()) return () => { cancelled = true; };
    let n = 0;
    const iv = setInterval(() => {
      if (cancelled || ready() || ++n > 20) clearInterval(iv);
    }, 150);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [currentPairId]);

  const handleSave = async (payload) => {
    setSaving(true);
    setSaveError("");
    setSavedFlash(false);
    setSavePending(false);
    try {
      const saved = await saveLcReview(payload);
      // Merge the saved review back into itemData so the form reflects it
      setItemData((prev) => ({ ...prev, existing_review: saved }));
      // Mark item as reviewed in the queue sidebar
      setQueueItems((prev) =>
        prev.map((item) =>
          item.pair_id === currentQueueItem.pair_id ? { ...item, reviewed: true } : item
        )
      );
      setSavedFlash(true);
      // Advance to the next unreviewed item (in filtered order) if available
      const curIdx = filteredItems.findIndex((item) => item.pair_id === currentQueueItem.pair_id);
      const next = filteredItems.find((item, idx) => idx > curIdx && !item.reviewed);
      if (next) {
        setTimeout(() => setCurrentPairId(next.pair_id), 600);
      }
    } catch (e) {
      if (isEndpointMissing(e)) setSavePending(true);
      else setSaveError("Failed to save review.");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncToSheet = async () => {
    setSyncingSheet(true);
    setSheetSyncError("");
    setSheetSyncPending(false);
    setSheetSyncResult(null);
    try {
      const result = await syncLcReviewToSheet(selectedBatch);
      setSheetSyncResult(result);
    } catch (e) {
      if (isEndpointMissing(e)) setSheetSyncPending(true);
      else setSheetSyncError("Failed to refresh the Sheet.");
    } finally {
      setSyncingSheet(false);
    }
  };

  const idxInFiltered = filteredItems.findIndex((it) => it.pair_id === currentPairId);
  const canPrev = idxInFiltered > 0;
  const canNext = idxInFiltered !== -1 && idxInFiltered < filteredItems.length - 1;
  const previewChallenge = itemData?.question
    ? buildPreviewChallenge(itemData.question)
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* Batch selector */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <span
          style={{
            textTransform: "uppercase",
            fontSize: "0.68em",
            fontWeight: 600,
            letterSpacing: "0.05em",
            color: "#8a8a8a",
          }}
        >
          Batch
        </span>
        {batchesPending ? (
          <EndpointPending label="LC review batches" />
        ) : (
          <select
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            disabled={queueLoading}
            style={{ padding: "6px 10px", borderRadius: "6px", border: "1px solid #ccc", fontSize: "0.9em" }}
          >
            {batches.length === 0 && <option value="">— no batches —</option>}
            {batches.map((b) => (
              <option key={b.name} value={b.name}>
                {b.name} {b.item_count != null ? `(${b.item_count})` : ""}
              </option>
            ))}
            <option value={ALL_BATCHES_VALUE}>All batches</option>
          </select>
        )}
        {queueLoading && <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#888" }} />}
        {queueItems.length > 0 && (
          <span style={{ fontSize: "0.85em", color: "#666" }}>
            {filteredItems.length} shown ({queueItems.length} loaded, {queueTotal} total)
          </span>
        )}

        <button
          onClick={handleSyncToSheet}
          disabled={syncingSheet || !selectedBatch}
          title="Manually export this batch's SME review results to the tracking Google Sheet (also runs automatically every 30 minutes)"
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            background: "white",
            border: `1px solid ${colors.nightlyWoods500 || "#ccc"}`,
            color: colors.nightlyWoods500 || "#333",
            borderRadius: "20px",
            padding: "6px 14px",
            cursor: syncingSheet || !selectedBatch ? "not-allowed" : "pointer",
            opacity: syncingSheet || !selectedBatch ? 0.6 : 1,
            fontSize: "13px",
          }}
        >
          <FontAwesomeIcon icon={faSync} spin={syncingSheet} />
          Refresh to Sheet
        </button>
      </div>

      {(sheetSyncResult || sheetSyncError || sheetSyncPending) && (
        <div style={{ fontSize: "0.85em" }}>
          {sheetSyncResult && (
            <span style={{ color: colors.green600 || "#2a9d5c" }}>
              Synced {sheetSyncResult.rows_written} row
              {sheetSyncResult.rows_written === 1 ? "" : "s"} to the Sheet.
            </span>
          )}
          {sheetSyncError && <span style={{ color: "#a33" }}>{sheetSyncError}</span>}
          {sheetSyncPending && <EndpointPending label="LC review Sheet sync" />}
        </div>
      )}

      {/* Filter controls */}
      {queueItems.length > 0 && (
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FieldLabel>Review Status</FieldLabel>
          <select
            value={reviewStatusFilter}
            onChange={(e) => setReviewStatusFilter(e.target.value)}
            disabled={queueLoading}
            style={selectStyle}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FieldLabel>CCSS</FieldLabel>
          <select
            value={ccssFilter}
            onChange={(e) => setCcssFilter(e.target.value)}
            disabled={queueLoading}
            style={selectStyle}
          >
            <option value="all">All</option>
            {ccssOptions.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FieldLabel>LC</FieldLabel>
          <select
            value={lcFilter}
            onChange={(e) => setLcFilter(e.target.value)}
            disabled={queueLoading}
            style={selectStyle}
          >
            <option value="all">All</option>
            {lcOptions.map((id) => (
              <option key={id} value={id}>
                {String(id).slice(0, 8)}…
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <FieldLabel>Question ID</FieldLabel>
          <input
            type="text"
            value={questionIdSearch}
            onChange={(e) => setQuestionIdSearch(e.target.value)}
            disabled={queueLoading}
            placeholder="Filter by question ID…"
            style={selectStyle}
          />
        </div>
      </div>
      )}

      {queuePending && <EndpointPending label="LC review queue" />}
      {queueError && <div style={{ color: "#a33", fontSize: "0.9em" }}>{queueError}</div>}

      {queueItems.length > 0 && filteredItems.length === 0 && (
        <div style={{ color: "#999", padding: "24px" }}>No items match the current filters.</div>
      )}

      {/* Three-column layout: queue sidebar | question render | review form */}
      {filteredItems.length > 0 && (
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
          {/* Queue sidebar */}
          <QueueSidebar
            items={filteredItems}
            currentPairId={currentPairId}
            onSelectPairId={(pairId) => setCurrentPairId(pairId)}
          />

          {/* Question render (left pane) */}
          <div style={{ flex: "100 1 420px", minWidth: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
            {/* Prev / Next navigation + position counter */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                onClick={() => {
                  if (idxInFiltered > 0) setCurrentPairId(filteredItems[idxInFiltered - 1].pair_id);
                }}
                disabled={!canPrev || itemLoading}
                style={{
                  background: "white",
                  border: `1px solid ${canPrev ? colors.nightlyWoods500 : "#ccc"}`,
                  color: canPrev ? colors.nightlyWoods500 : "#ccc",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  cursor: canPrev && !itemLoading ? "pointer" : "not-allowed",
                  fontSize: "13px",
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </button>
              <span style={{ fontSize: "0.85em", color: "#666" }}>
                {idxInFiltered + 1} of {filteredItems.length}
              </span>
              <button
                onClick={() => {
                  if (canNext) setCurrentPairId(filteredItems[idxInFiltered + 1].pair_id);
                }}
                disabled={!canNext || itemLoading}
                style={{
                  background: "white",
                  border: `1px solid ${canNext ? colors.nightlyWoods500 : "#ccc"}`,
                  color: canNext ? colors.nightlyWoods500 : "#ccc",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  cursor: canNext && !itemLoading ? "pointer" : "not-allowed",
                  fontSize: "13px",
                }}
              >
                Next <FontAwesomeIcon icon={faChevronRight} />
              </button>
              {savedFlash && (
                <span style={{ color: colors.green600 || "#2a9d5c", fontSize: "0.85em" }}>Saved.</span>
              )}
              {saveError && <span style={{ color: "#a33", fontSize: "0.85em" }}>{saveError}</span>}
              {savePending && <EndpointPending label="Review-save" />}
            </div>

            {itemLoading && (
              <div style={{ padding: "32px", textAlign: "center", color: "#888" }}>
                <FontAwesomeIcon icon={faSpinner} spin /> Loading…
              </div>
            )}
            {itemPending && !itemLoading && <EndpointPending label="LC review item" />}
            {itemError && !itemLoading && (
              <div style={{ color: "#a33", fontSize: "0.9em" }}>{itemError}</div>
            )}

            {previewChallenge && !itemLoading && (
              <div
                ref={previewRef}
                className="evaluator-mc-preview"
                style={{
                  border: "1px solid #eee",
                  borderRadius: "8px",
                  padding: "12px",
                  background: "#fff",
                }}
              >
                <ConsensusActivityRender
                  hideQuestion={false}
                  challenge={previewChallenge}
                  onMCAnswer={() => {}}
                  onORAnswer={() => {}}
                  isMathJaxSet={false}
                  showPromptFormula
                />
              </div>
            )}
          </div>

          {/* Review form (right pane) */}
          <div style={{ flex: "0 1 380px", minWidth: "320px" }}>
            {!itemLoading && currentQueueItem && (
              <LcReviewForm
                queueItem={currentQueueItem}
                itemData={itemData}
                saving={saving}
                onSave={handleSave}
              />
            )}
          </div>
        </div>
      )}

      {!queueLoading && !queuePending && queueItems.length === 0 && selectedBatch && !queueError && (
        <div style={{ color: "#999", padding: "24px" }}>
          No items in this batch.
        </div>
      )}
    </div>
  );
};
